export interface ScoreRingProps {
  score: number;
  size?: number;
  variant?: 'hero' | 'compact';
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--color-accent)';
  if (score >= 60) return 'var(--color-accent-gold)';
  return 'var(--color-accent-red)';
}

export function ScoreRing({ score, size = 180, variant = 'compact' }: ScoreRingProps) {
  const normalized = Math.max(0, Math.min(100, score));
  const color = scoreColor(normalized);

  if (variant === 'hero') {
    const r = 72;
    const stroke = 8;
    const circumference = 2 * Math.PI * r;
    const progress = (normalized / 100) * circumference;

    return (
      <svg
        width={180}
        height={180}
        viewBox="0 0 180 180"
        aria-label={`Score ${normalized} out of 100`}
        style={{ filter: `drop-shadow(0 0 14px ${color}55)` }}
      >
        <circle
          cx={90}
          cy={90}
          r={r}
          fill="none"
          stroke="var(--color-border-subtle)"
          strokeWidth={stroke}
        />
        <circle
          cx={90}
          cy={90}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dasharray 0.8s cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
        <text
          x={90}
          y={86}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--color-text-primary)"
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '38px',
            fontWeight: 700,
            letterSpacing: '-1px',
          }}
        >
          {Math.round(normalized)}
        </text>
        <text
          x={90}
          y={116}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="var(--color-text-muted)"
          style={{ fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 500 }}
        >
          / 100
        </text>
      </svg>
    );
  }

  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (normalized / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-border-subtle)"
          strokeWidth={3}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span
        className="absolute text-xs font-semibold"
        style={{ color, fontSize: size < 44 ? '10px' : '12px' }}
      >
        {Math.round(normalized)}
      </span>
    </div>
  );
}

export default ScoreRing;
